import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  UNSAFE_NavigationContext as NavigationContext,
  parsePath,
  useLocation,
} from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";

type PopupConfig = {
  title?: string;
  description?: string;
};

/**
 * Custom hook that handles navigation confirmation when user attempts to navigate away
 */
function useConfirmExit(confirmExit: () => Promise<boolean>, when = true) {
  const { navigator } = useContext(NavigationContext);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!when) {
      return;
    }

    const push = navigator.push;

    navigator.push = async (...args: Parameters<typeof push>) => {
      const [to] = args;
      const target = typeof to === "string" ? parsePath(to) : to;
      if (!target.pathname || target.pathname === pathname) {
        push(...args);
        return;
      }

      const result = await confirmExit();
      if (result !== false) {
        push(...args);
      }
    };

    return () => {
      navigator.push = push;
    };
  }, [navigator, confirmExit, when, pathname]);
}

export function usePrompt(popupConfig: PopupConfig, when = true) {
  const { t } = useTranslation();
  const savedRef = useRef(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Reset saved state when `when` changes to true
  useEffect(() => {
    if (when) {
      savedRef.current = false;
    }
  }, [when]);

  // Explicit function to mark the form as saved - call this after successful submission
  const markSaved = useCallback(() => {
    savedRef.current = true;
  }, []);

  const confirmExit = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      // If the form has been marked as saved, allow navigation without prompting
      if (savedRef.current) {
        resolve(true);
        return;
      }

      resolveRef.current = resolve;
      setIsOpen(true);
    });
  }, []);

  // Resolve the pending navigation and close the dialog. Any dismissal that
  // isn't an explicit "leave" (cancel button, Escape, overlay) means "stay".
  const settle = useCallback((shouldLeave: boolean) => {
    resolveRef.current?.(shouldLeave);
    resolveRef.current = null;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    // Only add beforeunload handler if prompt is active
    if (when && !savedRef.current) {
      window.onbeforeunload = function () {
        return;
      };
    } else {
      window.onbeforeunload = null;
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [when]);

  useConfirmExit(confirmExit, when && !savedRef.current);

  const confirmDialog = (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          settle(false);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{popupConfig.title}</AlertDialogTitle>
          {popupConfig.description && (
            <AlertDialogDescription>
              {popupConfig.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>
            {t("prompts.unsavedChanges.stay")}
          </AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={() => settle(true)}>
            {t("prompts.unsavedChanges.leave")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { markSaved, confirmDialog };
}
